/**
 * ExecutiveStandfirst — the Executive Report's brief (mockup carbon/01, the
 * Command Center's brief shape): the action-first h1, one lede, the view's
 * primary button and period control, and the provenance line (BriefHero),
 * beside the Guardian Score tile with its six-word verdict, the period delta
 * tag and the 100-point waffle (ScoreTile size="hero" + ScoreWaffle). The
 * columns stack below 1312px, with the tile capped at 640px.
 *
 * Copy comes from composeExecutiveReportBrief and reaches the page only
 * through BriefHero and toActionBrief (blueprint §4.7). The verdict renders
 * on the Guardian Score tile only (D22), upright and unquoted.
 */

import type { ReactNode } from 'react'
import {
  BriefHero,
  ScoreTile,
  ScoreWaffle,
  Skeleton,
  Tag,
  type BriefHeroButtonAction,
  type WaffleSegment,
} from '@/components/carbon'
import { toActionBrief } from '@/lib/brief-shape'
import type { ExecutiveReportBrief } from '@/lib/executive-report-brief'

/** The legacy ScoreBox bar. Accepted and no longer drawn (the waffle reads `segments`); Wave 3 removes it. */
interface ScoreComponent {
  label: string
  weight: string
  value: number
  fillClass: string
}

interface Props {
  brief: ExecutiveReportBrief
  /** Composite Guardian Score (0-100). */
  score: number | null
  /** Deprecated: the old ScoreBox bars. Accepted and not rendered. */
  components?: ScoreComponent[]
  /** The waffle's four groups (report-view reportSegments). Without them the tile shows no waffle. */
  segments?: WaffleSegment[]
  /** The Guardian Score's movement over the period ("+4 in 30 days"). */
  delta?: string | null
  /** The view's one primary button (Download PDF), drawn by BriefHero in place of the brief's link. */
  primaryAction?: BriefHeroButtonAction
  /** A view control at the end of the actions row (the period select); BriefHero keeps it while the brief loads. */
  controls?: ReactNode
  /** Deprecated: free-form actions, rendered as BriefHero's tertiary slot. Use `primaryAction` and `controls`. */
  actions?: ReactNode
  /** The provenance line under the actions. */
  provenance?: ReactNode
  isLoading?: boolean
}

export default function ExecutiveStandfirst({
  brief,
  score,
  segments,
  delta,
  primaryAction,
  controls,
  actions,
  provenance,
  isLoading = false,
}: Props) {
  const verdict = score == null ? undefined : toActionBrief(brief).verdict

  return (
    <section
      aria-labelledby="exec-standfirst-headline"
      className="mt-8 grid grid-cols-1 items-stretch gap-8 c-xlg:grid-cols-[minmax(0,1fr)_minmax(0,560px)]"
    >
      <BriefHero
        brief={brief}
        size="hero"
        headingId="exec-standfirst-headline"
        isLoading={isLoading}
        primaryAction={primaryAction}
        secondaryAction={actions}
        controls={controls}
        provenance={provenance}
        className="[overflow-wrap:anywhere]"
      />
      <ScoreTile
        size="hero"
        title="Guardian Score"
        score={score}
        verdict={verdict}
        delta={delta ? <Tag className="tabular-nums">{delta}</Tag> : undefined}
        isLoading={isLoading}
        aside={isLoading ? <Skeleton lines={4} width="158px" /> : segments ? <ScoreWaffle segments={segments} /> : undefined}
        className="max-c-xlg:max-w-[640px]"
      />
    </section>
  )
}
